import React, { FC, useRef, useState } from 'react';
import Buttons from '@components/Buttons/Buttons';
import CaretRight from '@components/Icons/CaretRight';
import Circle from '@components/Icons/Circle';
import CheckCircle from '@components/Icons/CheckCircle';
import { useRouter } from 'next/router';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import Divider from '@components/Divider';
import ProgressIndicator from '@components/ProgressIndicator';
import { DocumentRequestFileStatus } from '@typings/document-api-models';
import WarningCircle from '@components/Icons/WarningCircle';
import DocumentUploader, { DocumentUploaderRef } from '@components/Document/DocumentUploader';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import { logError } from '@providers/ErrorTracking';
import s from './DocumentFileCard.module.scss';

type Props = {
  documentRequestUserFileId?: string;
  documentId?: string | null;
  fileName: string;
  fileDescription: string | null;
  isEdit?: boolean;
  onFinishUpload?: () => Promise<void>;
  isRequired: boolean;
  status: DocumentRequestFileStatus;
};

const DocumentFileCard: FC<Props> = ({
  documentRequestUserFileId,
  fileName,
  fileDescription,
  documentId,
  isEdit = false,
  onFinishUpload,
  isRequired,
  status,
}) => {
  const [uploadError, setUploadError] = useState<Error | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const router = useRouter();
  const documentUploaderRef = useRef<DocumentUploaderRef>(null);
  const documentRequestUserId = deserializeQueryStringItem(router.query.documentRequestUserId);
  const creatorSlug = deserializeQueryStringItem(router.query.creatorSlug);

  const getText = (): string => {
    if (isEdit) {
      return 'Edit';
    }
    if (isUploading) {
      return 'Uploading...';
    }
    return 'Upload';
  };

  const handleClick = (): void => {
    if (!isEdit && !documentId) {
      documentUploaderRef.current?.uploadDocument();
    }
    if (documentId && documentRequestUserFileId && documentRequestUserId && creatorSlug) {
      router.push(
        getUrl(IDENTIFIERS.DOCUMENT_REQUEST_DOCUMENT_PREVIEW, {
          creatorSlug,
          documentRequestUserFileId,
          documentRequestUserId,
        }),
      );
    } else {
      logError(new Error('Could not go to next step from DocumentFileCard'), {
        creatorSlug,
        documentId,
        documentRequestUserFileId,
        documentRequestUserId,
      });
    }
  };

  return (
    <Buttons isWrapper isFullWidth onClick={handleClick} className={s.container}>
      <>
        <div className={s.top}>
          <div className={s.title}>
            {(isUploading && <ProgressIndicator size={20} />)
              || (status === DocumentRequestFileStatus.ACTION_REQUIRED && <WarningCircle weight='fill' color='var(--color-warning)' size={20} />)
              || (documentId && <CheckCircle weight="fill" color="var(--color-success)" size={20} />) || (
              <Circle color="var(--color-gray-25)" size={20} />
            )}
            <p className="caption-bold">
              {fileName} {!isRequired && <small>Optional</small>} {isRequired && <b>Required</b>}
            </p>
          </div>
          {documentId
            ? (
              <CaretRight />
            )
            : (
              <p className="caption-bold" style={{ color: 'var(--color-noodle)' }}>
                {getText()}
              </p>
            )}
        </div>
        {fileDescription && <>
          <Divider />
          <p className="caption">{fileDescription}</p>
        </>}
        {uploadError && (
          <p className="caption" style={{ color: 'var(--color-error)' }}>
            Failed to upload document. Please try again.
          </p>
        )}
      </>
      {documentRequestUserFileId && (
        <DocumentUploader
          ref={documentUploaderRef}
          documentRequestUserFileId={documentRequestUserFileId}
          setIsUploading={setIsUploading}
          setUploadError={setUploadError}
          onFinishUpload={onFinishUpload}
        />
      )}
    </Buttons>
  );
};

export default DocumentFileCard;
